import {
  GroupByPermissionParent,
  GroupByRolesType,
  GroupedPermissions,
  Permission,
  REMEDIATION_APPROVER_GROUPS_TYPES,
  REMEDIATION_GROUPS_TYPES,
  REMEDIATION_REQUESTER_GROUPS_TYPES,
  Role,
  RPermissions,
} from '../types';

import {
  PermissionGroupTypes,
  PermissionNames,
} from '@shared/interfaces/permissions.interface';
import {
  CustomUserClaim,
  decodeToken,
  isAccessToken,
  OktaAuth,
} from '@okta/okta-auth-js';
import { fromEventPattern, merge, Observable, startWith } from 'rxjs';

function isParentExists(
  acc: GroupedPermissions[],
  parent: GroupByPermissionParent,
): boolean {
  return !!acc.find((k: GroupedPermissions) => k.name === parent.name);
}

function constructParent(name: PermissionGroupTypes, desc: string) {
  return {
    description: desc,
    isParent: true,
    name: name,
  } as GroupedPermissions;
}

export function groupRolesByRoleType(roles: Role[]): GroupByRolesType {
  return {
    internalRoles: orderRolesByNumberOfPermissions(
      roles.filter((r) => r.isInternal),
    ),
    organizationRoles: roles.filter((r) => r.orgLevel && r.name !== 'External'),
    siteRoles: roles.filter((r) => r.siteLevel),
    studyRoles: roles.filter((r) => r.studyLevel),
  };
}

export function orderRolesByNumberOfPermissions(
  roles: Role[],
  asc = true,
): Role[] {
  function countPermissions(permissions: Permission[]) {
    let i = 0;
    for (const permission of permissions) {
      if (permission.canCreate) i++;
      if (permission.canEdit) i++;
      if (permission.canRemove) i++;
      if (permission.canView) i++;
    }
    return i;
  }
  return roles.sort((a, b) =>
    asc
      ? countPermissions(a.permissions) - countPermissions(b.permissions)
      : countPermissions(b.permissions) - countPermissions(a.permissions),
  );
}

export function groupPermissions(permissionList: RPermissions) {
  const getPermissionDescription = (key: string) => {
    return permissionList.labels[key]?.description ?? '';
  };
  return permissionList?.permissions?.reduce((acc, p) => {
    let k: GroupedPermissions;

    switch (p.name) {
      case PermissionNames.StudyDetail:
      case PermissionNames.SiteStudyDetail:
        k = constructParent(
          PermissionGroupTypes.studyPermission,
          getPermissionDescription('Study Permissions'),
        );
        if (!isParentExists(acc, k)) {
          acc.push({ ...k });
        }
        acc.push(p as GroupedPermissions);
        break;
      case PermissionNames.SiteDetail:
        k = constructParent(
          PermissionGroupTypes.sitePermission,
          getPermissionDescription('Site Permissions'),
        );
        if (!isParentExists(acc, k)) {
          acc.push({ ...k });
        }
        acc.push(p as GroupedPermissions);
        break;
      case PermissionNames.StudySubmission:
      case PermissionNames.SiteSubmission:
      case PermissionNames.NewSiteSubmission:
      case PermissionNames.AppealSubmission:
        k = constructParent(
          PermissionGroupTypes.submissionPermission,
          getPermissionDescription('Submission Permissions'),
        );
        if (!isParentExists(acc, k)) {
          acc.push({ ...k });
        }
        acc.push(p as GroupedPermissions);
        break;
      case PermissionNames.CancelSubmission:
        k = constructParent(
          PermissionGroupTypes.submissionPermission,
          getPermissionDescription('Submission Permissions'),
        );
        if (!isParentExists(acc, k)) {
          acc.push({ ...k });
        }
        acc.push(p as GroupedPermissions);
        break;
      case PermissionNames.StudyCorrespondence:
      case PermissionNames.SiteCorrespondence:
        k = constructParent(
          PermissionGroupTypes.collabhubPermission,
          getPermissionDescription('Collab Hub Permissions'),
        );
        if (!isParentExists(acc, k)) {
          acc.push({ ...k });
        }
        acc.push(p as GroupedPermissions);
        break;
      case PermissionNames.StudyTeam:
      case PermissionNames.SiteTeam:
        k = constructParent(
          PermissionGroupTypes.teamManagement,
          getPermissionDescription('Team Management Permissions'),
        );
        if (!isParentExists(acc, k)) {
          acc.push({ ...k });
        }
        acc.push(p as GroupedPermissions);
        break;
      case PermissionNames.PeopleAdmin:
      case PermissionNames.RoleAdmin:
      case PermissionNames.NotificationAdmin:
      case PermissionNames.QuestionnaireAdmin:
      case PermissionNames.AuditAdmin:
      case PermissionNames.ConfigurationAdmin:
      case PermissionNames.DocumentSuperAdmin:
      case PermissionNames.ImpersonateInternalUser:
      case PermissionNames.ImpersonateExternalUser:
      case PermissionNames.SubmissionSuperAdmin:
        k = constructParent(
          PermissionGroupTypes.adminPermission,
          getPermissionDescription('Administrator Permissions'),
        );
        if (!isParentExists(acc, k)) {
          acc.push({ ...k });
        }
        acc.push(p as GroupedPermissions);
        break;
      case PermissionNames.ViewOrganizationDetailEnum:
      case PermissionNames.ViewOrganizationEnum:
        k = constructParent(
          PermissionGroupTypes.organizationDetailsPermissions,
          getPermissionDescription('Organization Overview'),
        );
        if (!isParentExists(acc, k)) {
          acc.push({ ...k });
        }
        acc.push(p as GroupedPermissions);
        break;
      case PermissionNames.AddOrganizationAdminUserEnum:
      case PermissionNames.RemoveOrganizationAdminUserEnum:
      case PermissionNames.ViewOrganizationAdminUserEnum:
        k = constructParent(
          PermissionGroupTypes.organizationUserManagementPermissions,
          getPermissionDescription('Organization User Management'),
        );
        if (!isParentExists(acc, k)) {
          acc.push({ ...k });
        }
        acc.push(p as GroupedPermissions);
        break;
      case PermissionNames.ViewOrganizationStudiesEnum:
      case PermissionNames.ViewOrganizationSitesEnum:
        k = constructParent(
          PermissionGroupTypes.organizationAccessPermissions,
          getPermissionDescription('Organization Resource Access'),
        );
        if (!isParentExists(acc, k)) {
          acc.push({ ...k });
        }
        acc.push(p as GroupedPermissions);
        break;
      default:
        break;
    }

    return acc;
  }, [] as GroupedPermissions[]);
}

/**
 * Returns an observable that emits whenever the Okta token manager has a change in tokens.
 */
export const oktaTokenChanges$ = (oktaAuth: OktaAuth): Observable<void> => {
  const added$ = fromEventPattern<void>(
    (handler) => oktaAuth.tokenManager.on('added', handler),
    (handler) => oktaAuth.tokenManager.off('added', handler),
  );
  const renewed$ = fromEventPattern<void>(
    (handler) => oktaAuth.tokenManager.on('renewed', handler),
    (handler) => oktaAuth.tokenManager.off('renewed', handler),
  );
  const removed$ = fromEventPattern<void>(
    (handler) => oktaAuth.tokenManager.on('removed', handler),
    (handler) => oktaAuth.tokenManager.off('removed', handler),
  );
  return merge(added$, renewed$, removed$).pipe(startWith(undefined));
};

export const hasRemediationGroup = (
  oktaAuth: OktaAuth,
  permission: Set<REMEDIATION_GROUPS_TYPES>,
): boolean => {
  const token = oktaAuth.tokenManager.getTokensSync().accessToken;
  if (!token || !isAccessToken(token)) {
    return false;
  }
  // stored access tokens carry no decoded claims; read them from the JWT
  const groups: CustomUserClaim | CustomUserClaim[] | undefined = decodeToken(
    token.accessToken,
  ).payload['group'];

  const list = Array.isArray(groups) ? groups : groups ? [groups] : [];
  return list.some((group) =>
    permission.has(String(group) as REMEDIATION_GROUPS_TYPES),
  );
};

export const hasRemediationApproverGroup = (
  oktaAuth: OktaAuth,
  oktaGroupEnv: string,
): boolean =>
  hasRemediationGroup(oktaAuth, getRemediationApproverGroups(oktaGroupEnv));

export const hasRemediationRequesterGroup = (
  oktaAuth: OktaAuth,
  oktaGroupEnv: string,
): boolean =>
  hasRemediationGroup(oktaAuth, getRemediationRequesterGroups(oktaGroupEnv));

export const getRemediationApproverGroups = (
  oktaGroupEnv: string,
): Set<REMEDIATION_APPROVER_GROUPS_TYPES> =>
  new Set<REMEDIATION_APPROVER_GROUPS_TYPES>([
    `App-ERM-IT-Admin-${oktaGroupEnv}` as REMEDIATION_APPROVER_GROUPS_TYPES,
  ]);

export const getRemediationRequesterGroups = (
  oktaGroupEnv: string,
): Set<REMEDIATION_REQUESTER_GROUPS_TYPES> =>
  new Set<REMEDIATION_REQUESTER_GROUPS_TYPES>([
    `App-ERM-Business-Admin-${oktaGroupEnv}` as REMEDIATION_REQUESTER_GROUPS_TYPES,
  ]);

export const getRemediationGroups = (
  oktaGroupEnv: string,
): Set<REMEDIATION_GROUPS_TYPES> =>
  new Set<REMEDIATION_GROUPS_TYPES>([
    ...getRemediationApproverGroups(oktaGroupEnv),
    ...getRemediationRequesterGroups(oktaGroupEnv),
  ]);
